import React from "react";

import Loading from "ui/Loading";
import ExplainPanel from "features/Explain/ExplainPanel";

import { useQueryExplainQuery } from "./gql/Query.generated";
import { useQueryPlanQuery } from "./gql/Query.plan.generated";

import QueryExplainError, {
  QueryExplainLoadError,
} from "features/Explain/QueryExplainError";
import { ExplainPlanType } from "utils/explain";
import { ComparablePlanSummaryType } from "features/Explain/WithExplainPlan";

const QueryExplain = ({
  explainId,
  queryId,
  databaseId,
  blockSize,
  comparablePlans,
}: {
  explainId: string;
  queryId: string;
  databaseId: string;
  blockSize: number;
  comparablePlans: ComparablePlanSummaryType[];
}) => {
  // explainId either comes as humanId of QueryExplain, or "planid-originalplanid"
  const originalPlanId = explainId.split("planid-")[1];
  return originalPlanId ? (
    <QueryExplainWithPlan
      originalPlanId={originalPlanId}
      queryId={queryId}
      databaseId={databaseId}
      blockSize={blockSize}
      comparablePlans={comparablePlans}
    />
  ) : (
    <QueryExplainWithExplain
      explainId={explainId}
      databaseId={databaseId}
      blockSize={blockSize}
      comparablePlans={comparablePlans}
    />
  );
};

const QueryExplainWithExplain = ({
  explainId,
  databaseId,
  blockSize,
  comparablePlans,
}: {
  explainId: string;
  databaseId: string;
  blockSize: number;
  comparablePlans: ComparablePlanSummaryType[];
}) => {
  const { error, loading, data } = useQueryExplainQuery({
    variables: {
      explainId,
      databaseId,
    },
  });
  if (loading) {
    return <Loading />;
  }
  if (error) {
    return (
      <div className="m-5">
        <QueryExplainLoadError databaseId={databaseId} />
      </div>
    );
  }

  const explain = data.getQueryExplain;

  if (!explain) {
    return <Loading error />;
  }

  if (explain.error) {
    return (
      <div className="m-5">
        <QueryExplainError
          error={explain.error}
          source={explain.source}
          queryText={explain.querySample?.queryText}
        />
      </div>
    );
  }

  return (
    <ExplainPanel
      explain={explain}
      databaseId={databaseId}
      blockSize={blockSize}
      comparablePlans={comparablePlans}
    />
  );
};

const QueryExplainWithPlan = ({
  originalPlanId,
  queryId,
  databaseId,
  blockSize,
  comparablePlans,
}: {
  originalPlanId: string;
  queryId: string;
  databaseId: string;
  blockSize: number;
  comparablePlans: ComparablePlanSummaryType[];
}) => {
  const { error, loading, data } = useQueryPlanQuery({
    variables: {
      originalPlanId,
      queryId,
      databaseId,
    },
  });
  if (
    loading ||
    error ||
    !data.getQueryPlan ||
    !data.getQueryPlan.annotatedJson
  ) {
    return <Loading error={!!error} />;
  }
  const plan = data.getQueryPlan;
  const explain = {
    id: plan.id,
    humanId: plan.id,
    seenAt: plan.planCapturedTime,
    querySample: {
      id: plan.id,
      queryText: data.getQueryDetails.normalizedQuery,
      occurredAt: null,
      runtimeMs: null,
    },
    query: data.getQueryDetails,
    format: null,
    fingerprint: plan.planFingerprint,
    outputText: plan.explainPlan,
    annotatedJson: plan.annotatedJson,
    totalCost: plan.totalCost,
    totalSharedBlksRead: null,
    totalBlkReadTime: null,
  } as ExplainPlanType;
  return (
    <ExplainPanel
      explain={explain}
      databaseId={databaseId}
      blockSize={blockSize}
      comparablePlans={comparablePlans}
    />
  );
};

export default QueryExplain;
